import Immutable from 'immutable'
import { eventChannel } from 'redux-saga'
import * as actions from '../actions'
import * as requestTypes from './requestTypes'
import { createResponse, parseRequest, parseResponse } from './messageParser'
import { TerminalType } from '../data/Transaction'

const ALIVE_INTERVAL = 30
const ALIVE_TIMEOUT = 10
const IDLE_INTERVAL = 3

export class PaymentTerminal {
    _apiUrl: string | null = null
    _emitter: any = null
    _requests: Immutable.OrderedMap<string, any> = Immutable.OrderedMap()
    _socket: WebSocket | null = null
    _nextId = 1
    _pending = null
    _aliveTimer: any = null
    _aliveInterval: number = ALIVE_INTERVAL
    _aliveCounter = 0
    _aliveSent = false
    _idleCounter = 0
    _terminalId = null

    _close = () => {
        if (this._aliveTimer) {
            clearInterval(this._aliveTimer)
            this._aliveTimer = null
        }

        this._aliveCounter = 0
        this._idleCounter = 0
        this._aliveSent = false
        this._terminalId = null

        if (this._socket) {
            this._socket.onopen = null
            this._socket.onclose = null
            this._socket.onerror = null
            this._socket.onmessage = null
            this._socket.close()
        }

        this._socket = null
    }

    _createId = () => 'pt-' + this._nextId ++

    _createRequest = (method, params) => {
        return {
            jsonrpc: '2.0',
            id: this._createId(),
            method,
            params: {
                timestamp: new Date().toISOString(),
                ...params,
            },
        }
    }

    _createResponse = (id, result = {}) => {
        return {
            jsonrpc: '2.0',
            id,
            result,
        }
    }

    _emit = (action) => {
        if (this._emitter) {
            this._emitter(action)
        }
    }

    _aliveHandler = () => {
        if (this._socket) {
            this._aliveCounter ++
            this._idleCounter ++

            if (this._idleCounter >= IDLE_INTERVAL) {
                this._idleCounter = 0
                this._emit(actions.idle())
            }

            if (this._aliveSent && this._aliveCounter >= ALIVE_TIMEOUT) {
                this._aliveCounter = 0
                this.disconnect(1000, 'no response to keepalive')
            } else if (! this._aliveSent && this._aliveCounter >= this._aliveInterval) {
                this._aliveCounter = 0
                this.sendRequest(requestTypes.KEEPALIVE)
                this._aliveSent = true
            }
        }
    }

    _parseEventData = (event) => {
        try {
            return JSON.parse(event.data)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            this._emit(actions.error('invalid event data', event.data))
        }

        return null
    }

    _processRequest = (request) => {
        const response = createResponse(request)

        if (response !== null && request.id) {
            this.sendMessage(this._createResponse(request.id, response))
        } else {
            this._emit(parseRequest(request))
        }
    }

    _processResponse = (request, response) => {
        if (request.method === requestTypes.TERMINAL_INFO && response.result && response.result.terminal_id) {
            this._terminalId = response.result.terminal_id.toString()
        }

        const action = parseResponse(request, response)
        if (action.request && action.request.method === requestTypes.KEEPALIVE) {
            this._aliveSent = false
        } else {
            this._emit(action)
        }
    }

    _processMessage = (message) => {
        if (message.method && message.params) {
            this._processRequest(message)
        } else if (message.id) {
            const request = this._requests.get(message.id, null)
            if (request) {
                this._requests = this._requests.remove(message.id)
                this._processResponse(request, message)
            } else {
                this._emit(actions.error('response to unknown request', message))
            }
        }
    }

    configure = ({apiUrl, aliveInterval}: {apiUrl: string, aliveInterval?: number}) => {
        this._apiUrl = apiUrl
        this._aliveInterval = aliveInterval ?? ALIVE_INTERVAL
    }

    createEventChannel = () => {
        return eventChannel(emitter => {
            this._emitter = emitter

            return () => {
                return
            }
        })
    }

    connect = () => {
        this._close()

        this._socket = new WebSocket(this._apiUrl ?? '', ['jsonrpc2.0'])
        this._socket.onopen = () => {
            this._aliveTimer = setInterval(this._aliveHandler, 1000)
            this._emit(actions.connected())
            this.sendRequest(requestTypes.TERMINAL_INFO)
            this.sendRequest(requestTypes.STATUS)

            if (this._pending !== null) {
                const message = this._pending
                this._pending = null

                this.sendMessage(message, false)
            }
        }

        this._socket.onerror = (error) => {
            this._emit(actions.error('Socket error', error))
        }

        this._socket.onclose = () => {
            this._emit(actions.disconnected())
            this._pending = null
            this._close()
        }

        this._socket.onmessage = (event) => {
            const message = this._parseEventData(event)

            if (message) {
                if (message.method !== requestTypes.KEEPALIVE) {
                    this._aliveCounter = 0
                }
                this._idleCounter = 0
                this._processMessage(message)
            }
        }
    }

    disconnect = (code = 1000, reason: string | undefined = undefined) => {
        if (this._socket) {
            this._socket.close(code, reason)
            this._requests.clear()
        }
    }

    isConfigured = () => this._apiUrl !== null

    matchTerminal = (id, type) => {
        if (id) {
            return id === this._terminalId && type === TerminalType.POPLATEK
        } else if (type) {
            return type === TerminalType.POPLATEK
        } else {
            return true
        }
    }

    sendMessage = (message, autoconnect = true) => {
        if (this._socket !== null && this._socket.readyState === WebSocket.OPEN) {
            this._idleCounter = 0
            this._socket.send(JSON.stringify(message))
        } else if (autoconnect) {
            this._pending = message
            if (this._socket === null && this.isConfigured()) {
                this.connect()
            }
        }
    }

    sendRequest = (method, params = {}) => {
        const request = this._createRequest(method, params)

        this._requests = Immutable.OrderedMap<string, any>(this._requests.slice(- 50)).set(request.id, {method, params})

        this.sendMessage(request)
    }
}
